import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import {
  debounceTime,
  forkJoin,
  iif,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';
import {
  Category,
  EventResponseEvent,
} from 'src/app/types/event-response.type';
import { map } from 'rxjs/operators';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { CategoriesHttpService } from 'src/app/services/categories.http';
import { EventsHttpService } from '../../../services/events.http.service';
import { BadgeType } from '../../../components/badge/types/badge.type';
import { ActivatedRoute } from '@angular/router';
import { NavigationService } from '../../../services/navigation.service';
import { UtilsService } from '../../../services/utils.service';
import { unwrapCategoriesUtil } from '../../../utils/unwrapCategories.util';

interface SearchQueryForm {
  searchQuery: FormControl<string>;
}

export interface SearchQuerySelectedState {
  categories: Category[];
  events: EventResponseEvent[];
  searchTerm: string;
}

const initialState: SearchQuerySelectedState = {
  categories: [],
  events: [],
  searchTerm: '',
};

@Component({
  selector: 'app-search-query-dropdown',
  templateUrl: './search-query-dropdown.component.html',
  styleUrls: ['./search-query-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchQueryDropdownComponent implements AfterViewInit {
  @Input()
  initialCategories: Category[] = [];

  @Input()
  isMobile: boolean = false;

  @ViewChild('searchDropdown', { static: true })
  searchDropdown: NgbDropdown | null = null;

  @Output()
  categorySelection: EventEmitter<Category[]> = new EventEmitter<Category[]>();

  @Output()
  searchQueryChange: EventEmitter<string> = new EventEmitter<string>();

  @Output()
  openChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  get allCategories(): Category[] {
    return unwrapCategoriesUtil(this.initialCategories);
  }

  showMoreCategories: boolean = false;
  showMoreEvents: boolean = false;
  defaultEventListCount = 3;
  defaultCategoryListCount = 4;
  isOpen: boolean = false;

  selectedState: SearchQuerySelectedState = JSON.parse(
    JSON.stringify(initialState)
  );

  innerForm = new FormGroup<SearchQueryForm>({
    searchQuery: new FormControl('', {
      nonNullable: true,
    }),
  });

  results$: Observable<SearchQuerySelectedState> =
    this.innerForm.controls.searchQuery.valueChanges.pipe(
      debounceTime(200),
      tap((searchTerm) => this.searchQueryChange.emit(searchTerm)),
      switchMap((term) => {
        const searchTerm = term.toLowerCase();

        return forkJoin({
          categories: iif(
            () => term.length > 2,
            this.categories$,
            of(this.allCategories)
          ),
          events: iif(
            () => term.length > 2,
            this.eventsHttp.getEvents({ search: searchTerm }),
            of([])
          ),
          searchTerm,
        });
      })
    );

  private get categories$(): Observable<Category[]> {
    return this.categoriesHttp
      .getUnwrappedCategories({
        category_title: this.innerForm.controls.searchQuery.value,
      })
      .pipe(
        map((categories) => categories),
        tap(this.setSelectionStateFromQueryParams)
      );
  }

  constructor(
    private categoriesHttp: CategoriesHttpService,
    private eventsHttp: EventsHttpService,
    private route: ActivatedRoute,
    private navigationService: NavigationService,
    private cdr: ChangeDetectorRef
  ) {}

  setSelectionStateFromQueryParams(): void {
    const queryParams = this.route?.snapshot.queryParams;

    if (queryParams && 'categories' in queryParams) {
      const categoryParams: string[] | string = queryParams['categories'];
      const preselectedCategories: number[] = Array.isArray(categoryParams)
        ? categoryParams.map(Number)
        : [Number(categoryParams)];

      this.selectedState.categories = this.allCategories.filter((category) =>
        preselectedCategories.includes(category.category_id)
      );

      if (this.isCategorySelected) {
        this.clearSearchInput();
      }

      this.cdr.detectChanges();
    }
  }

  ngAfterViewInit(): void {
    this.route.queryParams.subscribe((queryParams) => {
      if (queryParams && 'search' in queryParams) {
        this.innerForm.controls.searchQuery.setValue(queryParams['search']);
      }

      this.setSelectionStateFromQueryParams();
    });
  }

  get selectedCategoryBadges(): BadgeType[] {
    return this.selectedState.categories.map(
      ({ category_title, category_id }) => ({
        text: category_title,
        color: 'dark',
        closable: true,
        id: category_id,
      })
    );
  }

  get isCategorySelected(): boolean {
    return !!this.selectedState.categories.length;
  }

  get searchTerm(): string {
    return this.innerForm.controls.searchQuery.value;
  }

  get isSearchTermEmpty(): boolean {
    return this.searchTerm.length === 0;
  }

  get forceNoResults(): boolean {
    return (
      this.innerForm.controls.searchQuery.value.length > 0 &&
      this.innerForm.controls.searchQuery.value.length < 3
    );
  }

  getUnselectedCategories(categories: Category[]): Category[] {
    const countByDefault =
      this.isCategorySelected || this.isSearchTermEmpty
        ? this.defaultCategoryListCount
        : 3;

    const countWhenShowMore = 1000;

    const filtered =
      categories?.filter(
        (category: Category) =>
          !this.selectedState.categories.some(
            ({ category_id }) => category_id === category.category_id
          )
      ) ?? [];

    if (this.isCategorySelected) {
      return filtered;
    }

    return filtered.slice(
      0,
      this.showMoreCategories ? countWhenShowMore : countByDefault
    );
  }

  onCategoryAdd(category: Category): void {
    this.selectedState.categories.push(category);
    this.categorySelection.emit(this.selectedState.categories);
  }

  onCategoryRemove(categoryId: number): void {
    this.selectedState.categories = this.selectedState.categories.filter(
      ({ category_id }) => category_id !== categoryId
    );
    this.categorySelection.emit(this.selectedState.categories);
  }

  clearAll(): void {
    this.selectedState.categories = [];
    this.clearSearchInput();
    this.categorySelection.emit(this.selectedState.categories);
  }

  goToEventPage({ event_id }: EventResponseEvent): void {
    this.navigationService.goToEventPage(event_id);
  }

  onInputFocus($event: MouseEvent, searchDropdown: NgbDropdown): void {
    let toReset = false;
    if (this.innerForm.controls.searchQuery.value.length === 0) {
      this.innerForm.controls.searchQuery.setValue(' '); // empty space
      toReset = true;
    }

    setTimeout(() => {
      searchDropdown.open();
      this.isOpen = true;

      if (toReset) {
        this.clearSearchInput();
      }
    }, 300);

    this.showMoreEvents = false;
    this.showMoreCategories = false;
  }

  getEventList(events: EventResponseEvent[]): EventResponseEvent[] {
    return events.slice(
      0,
      this.showMoreEvents ? 1000 : this.defaultEventListCount
    );
  }

  onOpenChange(isOpen: boolean): void {
    this.isOpen = isOpen;
    this.openChange.emit(isOpen);

    if (!this.isOpen && this.isCategorySelected) {
      this.clearSearchInput();
    }
  }

  parseLocation(eventLocation: string): string {
    return UtilsService.getLocationDisplayName(eventLocation);
  }

  clearSearchInput(): void {
    this.innerForm.controls.searchQuery.setValue('');
  }
}
